import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
    Box, Flex, Text, Link
} from 'components/ui';
import { colors, radii } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import resourceWrapper from 'utils/framework/resourceWrapper';
import basketConstants from 'constants/Basket';
import Actions from 'Actions';
import languageLocale from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import { HEADER_VALUE } from 'constants/authentication';
import Location from 'utils/Location';
import helpersUtils from 'utils/Helpers';
import bccUtils from 'utils/BCC';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import SDDRougeTestV2InfoModal from 'utils/SDDRougeTestV2InfoModal';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import { isString } from 'utils/functions/isString';
import FulfillmentCard from 'components/ProductPage/FulfillmentMethods/FulfillmentCard';
import ShippingDetailsPanel from 'components/ProductPage/FulfillmentMethods/ShippingDetailsPanel';
import FastDeliveryDetailsPanel from 'components/ProductPage/FulfillmentMethods/FastDeliveryDetailsPanel';
import PickupDetailsPanel from 'components/ProductPage/FulfillmentMethods/PickupDetailsPanel';
import AutoReplenishRow from 'components/ProductPage/FulfillmentMethods/AutoReplenishRow';
import SDULandingPageModal from 'components/GlobalModals/SDULandingPageModal';
import useFulfillmentMethodsProps from 'components/ProductPage/FulfillmentMethods/hooks/useFulfillmentMethodsProps';
import { formatCardEta, formatPanelEta } from 'components/ProductPage/FulfillmentMethods/utils/formatEta';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { DELIVERY_OPTIONS } = basketConstants;
const { deferTaskExecution } = helpersUtils;
const { SAME_DAY_UNLIMITED_MODAL_US, SAME_DAY_UNLIMITED_MODAL_CA } = bccUtils.MEDIA_IDS;
const getText = resourceWrapper(languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR));

const styles = {
    root: {
        backgroundColor: colors.nearWhite
    },
    divider: {
        height: 1,
        width: '100%',
        backgroundColor: colors.lightGray
    }
};

/**
 * Strips markdown asterisks from text for accessibility (aria-label).
 * Removes both double (`**bold**`) and single (`*italic*`) asterisk formatting
 * so screen readers don't announce "star star getasap star star".
 *
 * @param {string} text - Text that may contain markdown asterisks
 * @returns {string} Text with all asterisks removed
 *
 * @example
 * stripMarkdownAsterisks('FREE with code **getasap**')
 * // => 'FREE with code getasap'
 */
const stripMarkdownAsterisks = text => {
    if (!text || !isString(text)) {
        return text;
    }

    return text.replace(/\*/g, '');
};

/**
 * FreeShippingSignInTitle
 *
 * Signed-out title for the Free Shipping card (SHP-5258): "Sign in" as a real,
 * independently-focusable link followed by "for" and the bold card title.
 * Rendered only when `FulfillmentCard`'s `interactive` variant is active.
 *
 * @param {object} props
 * @param {Function} props.onSignInClick - Opens the sign-in modal; also stops
 *     propagation so the click doesn't toggle the card's own radio selection.
 * @returns {JSX.Element}
 */
const FreeShippingSignInTitle = ({ onSignInClick }) => (
    <Text
        is='p'
        fontSize='base'
        lineHeight='tight'
    >
        {getText(
            'freeShippingSignInCardText',
            false,
            <Link
                onClick={onSignInClick}
                color='blue'
                underline={true}
                fontSize='base'
                padding={2}
                margin={-2}
                children={getText('signInLinkLabel')}
            />
        )}{' '}
        <Text
            is='span'
            fontWeight='bold'
            color='black'
            children={getText('freeShippingCardTitle')}
        />
    </Text>
);

FreeShippingSignInTitle.propTypes = {
    onSignInClick: PropTypes.func.isRequired
};

/**
 * Builds the Pickup card's ETA text.
 * @param {object} params
 * @returns {string} The Pickup card's ETA text.
 */
const buildPickUpCardETA = ({ isPickUpSelected, storeAvailabilityText, storeName }) => {
    let pickupCardEta = getText('pickupCardEta');

    if (storeAvailabilityText) {
        pickupCardEta = storeAvailabilityText;
    }

    //The unselected Pickup card displays the store location name as the ETA text
    if (!isPickUpSelected && storeName) {
        pickupCardEta = storeName;
    }

    return pickupCardEta;
};

/**
 * Builds the Pickup card's secondary text. Extracted from the component body
 * (alongside `buildCards`) so the growing number of Pickup-unavailable
 * scenarios — gift card / online-only (SHP-5234/5262), non-BOPIS store /
 * non-BOPISable item (SHP-5361), and in-stock/limited-stock/out-of-stock at the
 * selected store (SHP-5232/5280/5525) — doesn't push `FulfillmentMethods` over the
 * complexity budget. Priority: disabled-card reasons first, then BOPIS
 * eligibility (a more fundamental blocker than stock), then stock status.
 *
 * @param {object} params
 * @returns {string|undefined} The Pickup card's secondary text.
 */
const buildPickupCardSecondaryText = ({
    isGiftCard,
    isOnlineOnly,
    isPickupUnavailableAtStore,
    isPickupUnavailableForItem,
    storeAvailabilityText,
    outOfStockText
}) => {
    if (isGiftCard || isOnlineOnly) {
        return getText('pickupNotAvailable');
    }

    if (isPickupUnavailableAtStore) {
        return getText('checkNearbyStores');
    }

    if (isPickupUnavailableForItem) {
        return getText('pickupNotAvailable');
    }

    if (storeAvailabilityText) {
        return storeAvailabilityText;
    }

    return outOfStockText;
};

const buildFastDeliveryCardFields = ({
    isGiftCard,
    isOnlineOnly,
    isFastDeliveryOutOfStock,
    isFastDeliveryZipUnavailable,
    isFastDeliveryPermanentlyUnavailable,
    isFastDeliveryTemporarilyUnavailable,
    preferredZipCode,
    sameDaySubTitle
}) => {
    const disabled = isGiftCard || isOnlineOnly || isFastDeliveryPermanentlyUnavailable || isFastDeliveryTemporarilyUnavailable;
    const hasMutedText = disabled || isFastDeliveryOutOfStock || isFastDeliveryZipUnavailable;
    const zipText = preferredZipCode || getText('yourLocation');
    const secondaryText =
        isGiftCard || isOnlineOnly || isFastDeliveryPermanentlyUnavailable
            ? getText('fastDeliveryNotAvailable')
            : isFastDeliveryTemporarilyUnavailable
                ? getText('fastDeliveryTemporarilyUnavailable')
                : isFastDeliveryOutOfStock
                    ? getText('outOfStockForZip', false, zipText)
                    : isFastDeliveryZipUnavailable
                        ? getText('notAvailableInZip', false, zipText)
                        : undefined;

    return {
        disabled,
        eta: hasMutedText ? undefined : sameDaySubTitle,
        secondaryText,
        etaColor: hasMutedText ? 'gray' : 'green'
    };
};

/**
 * Builds the 3-card radiogroup config (Free Shipping / Fast Delivery / Pickup).
 * Extracted from the component body so the gift-card/online-only/signed-out
 * branching doesn't push `FulfillmentMethods` over the complexity budget.
 *
 * @param {object} params
 * @returns {Array<object>} Card configs consumed by `FulfillmentCard`.
 */
const buildCards = ({
    isSignedIn,
    onSignInClick,
    shippingCardEta,
    sameDayTitleText,
    isGiftCard,
    fastDeliveryCardDisabled,
    fastDeliveryCardEta,
    fastDeliveryCardSecondaryText,
    fastDeliveryCardEtaColor,
    isOnlineOnly,
    pickupCardEta,
    pickupCardSecondaryText,
    pickupMessageColor
}) => [
    {
        id: 'fulfillmentMethod-standard',
        method: DELIVERY_OPTIONS.STANDARD,
        iconName: 'shippingTruckOutline',
        activeIconName: 'shippingTruck',
        title: isSignedIn
            ? getText('freeShippingCardTitle')
            : getText('freeShippingSignInAriaLabel', false, getText('signInLinkLabel'), getText('freeShippingCardTitle')),
        titleContent: isSignedIn ? undefined : <FreeShippingSignInTitle onSignInClick={onSignInClick} />,
        interactive: !isSignedIn,
        etaText: shippingCardEta,
        etaColor: 'green'
    },
    {
        id: 'fulfillmentMethod-fastDelivery',
        method: DELIVERY_OPTIONS.SAME_DAY,
        iconName: 'bag',
        activeIconName: 'bagActive',
        title: sameDayTitleText,
        disabled: fastDeliveryCardDisabled,
        secondaryText: fastDeliveryCardSecondaryText,
        etaText: fastDeliveryCardEta,
        etaColor: fastDeliveryCardEtaColor,
        enableMarkdownSubtitle: true
    },
    {
        id: 'fulfillmentMethod-pickup',
        method: DELIVERY_OPTIONS.PICKUP,
        iconName: 'store',
        activeIconName: 'storeActive',
        title: isGiftCard ? getText('availableInAllStoresCardTitle') : isOnlineOnly ? getText('onlineOnly') : getText('pickupCardTitle'),
        disabled: isGiftCard || isOnlineOnly,
        secondaryText: pickupCardSecondaryText,
        etaText: isGiftCard || isOnlineOnly ? undefined : pickupCardEta,
        etaColor: pickupMessageColor
    }
];

/**
 * FulfillmentMethods
 *
 * PDP Fulfillment Methods redesign (SHP-5111): a 3-card radiogroup (Free
 * Shipping / Fast Delivery / Pickup), the shipping details panel, and the
 * Auto-Replenish row. SHP-5258 adds a signed-out "Sign in" prompt to the Free
 * Shipping card and gates its selection styling behind `userUtils.isSignedIn()`.
 * SHP-5262 relabels the Pickup card to "Online Only" and disables it for
 * online-only SKUs, mirroring the SHP-5234 gift-card treatment.
 * SHP-5238 adds the Pickup selected state (details panel + store card text).
 * SHP-5451 gives the Pickup and Fast Delivery cards filled glyphs while selected.
 * SHP-5408 extends the same treatment to Free Shipping: `shippingTruckOutline`
 * (added in the same story to match Figma) is the default glyph and
 * `shippingTruck` (filled) is swapped in while selected.
 *
 * @param {object} props
 * @param {object} [props.currentProduct] - The current product from Redux.
 * @param {object} [props.fulfillment] - Fulfillment selection handlers from `useFulfillment`.
 * @param {object} [props.commerce] - Commerce state from `useCommerce`, forwarded to `AutoReplenishRow`.
 * @param {boolean} [props.displayOrderCutoffCountdown] - Forwarded to `useFulfillmentMethodsProps` for the Fast Delivery cutoff countdown.
 * @returns {JSX.Element}
 */
/* eslint-disable-next-line complexity */
const FulfillmentMethods = ({ currentProduct, fulfillment, commerce, displayOrderCutoffCountdown }) => {
    // SHP-5111 delivers the visual starting point: Free Shipping is preselected;
    // Fast Delivery / Pickup selected states arrive in follow-up stories.
    const [selectedMethod, setSelectedMethod] = useState(DELIVERY_OPTIONS.STANDARD);
    const [renderSDULandingPage, setRenderSDULandingPage] = useState(false);

    const dispatch = useDispatch();
    const isSignedIn = userUtils.isSignedIn();

    const {
        preferredZipCode,
        shipToHomeMessage,
        showAutoReplenishment,
        isReplenishmentEligible,
        isGiftCard,
        isOnlineOnly,
        sddRadioButtonDisabled,
        availabilityText,
        locationText,
        forText,
        sameDayAvailabilityStatus,
        availabilityTextColor,
        sameDayDeliveryPanelMessage,
        sddRougeTestV2FreeShippingMessage,
        sddRougeTestThreshold,
        nddEnabled,
        isUserSduTrialEligible,
        isUserSDUTrialAllowed,
        isSDUAddedToBasket,
        skuTrialEligibility,
        skuTrialPeriod,
        sameDayTitleText,
        sameDaySubTitle,
        storeName,
        availabilityLabel,
        availabilityColor,
        hasStoreAvailability,
        pickupMessage,
        pickupMessageColor,
        showInStorePickup,
        showCurbsideIndicator,
        showConciergeCurbsideIndicator,
        isInStockAtNearbyStore,
        showNearbyStoreAvailability,
        isPickupOutOfStock,
        isPickupUnavailableAtStore,
        isPickupUnavailableForItem,
        isFastDeliveryOutOfStock,
        isFastDeliveryZipUnavailable,
        isFastDeliveryPermanentlyUnavailable,
        isFastDeliveryTemporarilyUnavailable,
        isPickUpSelected
    } = useFulfillmentMethodsProps(currentProduct, displayOrderCutoffCountdown, fulfillment, selectedMethod);

    const isFastDeliveryUnavailable = isFastDeliveryOutOfStock || isFastDeliveryZipUnavailable;

    const currentSku = currentProduct?.currentSku;

    // SHP-5258: signed-out clients see a "Sign in" prompt on the Free Shipping card
    // since eligibility is tied to Beauty Insider membership. `stopPropagation` keeps
    // the nested link from also toggling the card's own radio selection.
    const showSignInModal = useCallback(
        () => dispatch(Actions.showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } })),
        [dispatch]
    );

    const handleSignInLinkClick = useCallback(
        e => {
            e.stopPropagation();
            showSignInModal();
        },
        [showSignInModal]
    );

    const fireShippingOptionClickAnalytics = useCallback(
        (method, isAutoReplenishSelected = false) => {
            if (typeof digitalData !== 'object' || !digitalData?.page?.attributes) {
                return;
            }

            let actionInfo;

            if (method === DELIVERY_OPTIONS.STANDARD && isAutoReplenishSelected) {
                actionInfo = anaConsts.EVENT_NAMES.AUTO_REPLENISH_RADIO_BUTTON_CLICK;
            } else {
                switch (method) {
                    case DELIVERY_OPTIONS.STANDARD:
                        actionInfo = anaConsts.EVENT_NAMES.FREE_SHIPPING_RADIO_BUTTON_CLICK;

                        break;
                    case DELIVERY_OPTIONS.SAME_DAY:
                        actionInfo = anaConsts.EVENT_NAMES.SAME_DAY_DELIVERY_RADIO_BUTTON_CLICK;

                        break;
                    case DELIVERY_OPTIONS.PICKUP:
                        actionInfo = anaConsts.EVENT_NAMES.BUY_ONLINE_AND_PICK_UP_RADIO_BUTTON_CLICK;

                        break;

                    default:
                        return;
                }
            }

            digitalData.page.attributes.outOfStock = '';

            if (currentProduct?.currentSku?.isOutOfStock && method === DELIVERY_OPTIONS.STANDARD) {
                digitalData.page.attributes.outOfStock = `Yes - ${anaConsts.FULFILLMENT_TYPE.Standard}`;
            }

            pdpAnalytics.trackLinkClick({
                actionInfo,
                prop44: digitalData.page.attributes.outOfStock,
                productStrings: `;${currentProduct?.currentSku?.skuId};;;;eVar26=${currentProduct?.currentSku?.skuId}`
            });
        },
        [currentProduct]
    );

    const handleSelect = useCallback(
        (method, { trackAnalytics = true } = {}) => {
            setSelectedMethod(method);

            deferTaskExecution(() => {
                commerce?.autoReplenishClick?.(false);

                switch (method) {
                    case DELIVERY_OPTIONS.STANDARD:
                        fulfillment?.selectShipped?.();

                        if (trackAnalytics) {
                            fireShippingOptionClickAnalytics(method, commerce?.autoReplenishChecked);
                        }

                        break;
                    case DELIVERY_OPTIONS.SAME_DAY:
                        fulfillment?.selectSameDay?.(fulfillment?.sameDayAvailable);

                        if (trackAnalytics) {
                            fireShippingOptionClickAnalytics(method);
                        }

                        break;
                    case DELIVERY_OPTIONS.PICKUP:
                        fulfillment?.selectPickup?.();

                        if (trackAnalytics) {
                            fireShippingOptionClickAnalytics(method);
                        }

                        break;
                    default:
                        break;
                }
            });
        },
        [commerce, fulfillment, fireShippingOptionClickAnalytics]
    );

    // Guards against a stale selection when the mounted SKU becomes a gift card
    // or online-only item (e.g. a shade/variant change) while Fast Delivery/Pickup
    // was selected — those cards become disabled, so the selection must fall back
    // to shipping. Routed through `handleSelect` (not just `setSelectedMethod`) so
    // the shared `fulfillment` state (e.g. `basketType`) stays in sync with the UI;
    // `trackAnalytics: false` since this is a forced fallback, not a user click.
    const fastDeliverySelectable = !isFastDeliveryPermanentlyUnavailable && !isFastDeliveryTemporarilyUnavailable;
    useEffect(() => {
        const forceStandardFromFastDelivery = selectedMethod === DELIVERY_OPTIONS.SAME_DAY && !fastDeliverySelectable;

        if (((isGiftCard || isOnlineOnly) && selectedMethod !== DELIVERY_OPTIONS.STANDARD) || forceStandardFromFastDelivery) {
            handleSelect(DELIVERY_OPTIONS.STANDARD, { trackAnalytics: false });
        }
    }, [isGiftCard, isOnlineOnly, fastDeliverySelectable, selectedMethod, handleSelect]);

    const toggleSDULandingPage = useCallback(() => setRenderSDULandingPage(prev => !prev), []);

    const handleShowSDDRougeTestV2InfoModal = useCallback(() => {
        SDDRougeTestV2InfoModal.showModal(toggleSDULandingPage, sddRougeTestThreshold);
    }, [toggleSDULandingPage, sddRougeTestThreshold]);

    const handleFastDeliveryLocationClick = useCallback(() => {
        const entry = Location.isProductPage() ? 'ppage sdd' : '';
        dispatch(
            Actions.showShippingDeliveryLocationModal({
                isOpen: true,
                entry,
                callback: ({ sameDayAvailable: sda }) => {
                    fulfillment?.selectSameDay?.(sda, true);
                },
                sduZipcodeModal: false,
                sku: currentProduct?.currentSku
            })
        );
    }, [dispatch, fulfillment, currentProduct]);

    // When the item is out of stock for standard shipping, do not display the delivery ETA
    // text under the "Free Shipping" tile label
    const shippingCardEta = currentSku?.isOutOfStock ? undefined : formatCardEta(shipToHomeMessage);
    const panelEtaText = formatPanelEta(shipToHomeMessage);

    const {
        disabled: fastDeliveryCardDisabled,
        eta: fastDeliveryCardEta,
        secondaryText: fastDeliveryCardSecondaryText,
        etaColor: fastDeliveryCardEtaColor
    } = buildFastDeliveryCardFields({
        isGiftCard,
        isOnlineOnly,
        isFastDeliveryOutOfStock,
        isFastDeliveryZipUnavailable,
        isFastDeliveryPermanentlyUnavailable,
        isFastDeliveryTemporarilyUnavailable,
        preferredZipCode,
        sameDaySubTitle
    });

    // Per Figma, when the item is available at the selected store the Pickup card
    // secondary text shows the store availability instead of the default static ETA.
    // Limited stock counts as available, and the card echoes the same label the details
    // panel renders, so it reads "Limited Stock at {store}" there (SHP-5525).
    const isAvailableAtSelectedStore =
        hasStoreAvailability && Boolean(ExtraProductDetailsUtils.isLabelInStockStatus(availabilityLabel)) && Boolean(storeName);
    const storeAvailabilityText = isAvailableAtSelectedStore ? `${getText(availabilityLabel)} ${getText('pickupPanelAt')} ${storeName}` : undefined;
    const pickupCardEta = buildPickUpCardETA({ isPickUpSelected, storeAvailabilityText, storeName });

    // The card shows "Out of Stock at {store}" whenever the item is OOS at the selected store,
    // in every scenario — no-nearby (SHP-5232) and nearby (SHP-5280) alike.
    const outOfStockText = isPickupOutOfStock ? (storeName ? getText('outOfStockAt', false, storeName) : getText('outOfStock')) : undefined;
    const pickupCardSecondaryText = buildPickupCardSecondaryText({
        isGiftCard,
        isOnlineOnly,
        isPickupUnavailableAtStore,
        isPickupUnavailableForItem,
        storeAvailabilityText,
        outOfStockText
    });

    const cards = buildCards({
        isSignedIn,
        onSignInClick: handleSignInLinkClick,
        shippingCardEta,
        sameDayTitleText,
        isGiftCard,
        fastDeliveryCardDisabled,
        fastDeliveryCardEta,
        fastDeliveryCardSecondaryText,
        fastDeliveryCardEtaColor,
        isOnlineOnly,
        pickupCardEta,
        pickupCardSecondaryText,
        pickupMessageColor
    });

    const showFastDeliveryPanel = selectedMethod === DELIVERY_OPTIONS.SAME_DAY && fastDeliverySelectable;

    const sduMediaId = languageLocale.isCanada() ? SAME_DAY_UNLIMITED_MODAL_CA : SAME_DAY_UNLIMITED_MODAL_US;

    if (typeof digitalData === 'object' && digitalData && digitalData?.page?.attributes) {
        digitalData.page.attributes.isSameDayDeliveryAvailable = !sddRadioButtonDisabled;
    }

    return (
        <Box
            is='fieldset'
            marginTop={4}
            marginBottom={4}
            marginX={['-container', 0]}
            padding={4}
            borderRadius={radii[1]}
            lineHeight='tight'
            css={styles.root}
        >
            <Flex
                flexDirection='column'
                gap={3}
                width='100%'
            >
                <Flex
                    flexDirection='column'
                    gap={2}
                    width='100%'
                >
                    <Flex
                        role='radiogroup'
                        aria-label={getText('fulfillmentMethodsGroupAriaLabel')}
                        gap={2}
                        width='100%'
                        alignItems='stretch'
                    >
                        {cards.map(card => {
                            const secondary = card.secondaryText || card.etaText;
                            // Strip markdown asterisks from aria-label so VoiceOver doesn't read "star star getasap star star"
                            const ariaLabel = secondary ? `${card.title}, ${stripMarkdownAsterisks(secondary)}` : card.title;

                            return (
                                <Box
                                    key={card.id}
                                    display='flex'
                                    flex='1 1 0'
                                    minWidth={0}
                                >
                                    <FulfillmentCard
                                        id={card.id}
                                        iconName={card.iconName}
                                        activeIconName={card.activeIconName}
                                        title={card.title}
                                        titleContent={card.titleContent}
                                        etaText={card.etaText}
                                        etaColor={card.etaColor}
                                        secondaryText={card.secondaryText}
                                        disabled={card.disabled}
                                        isSelected={selectedMethod === card.method}
                                        onSelect={() => handleSelect(card.method)}
                                        ariaLabel={ariaLabel}
                                        interactive={card.interactive}
                                        enableMarkdownSubtitle={card.enableMarkdownSubtitle}
                                    />
                                </Box>
                            );
                        })}
                    </Flex>
                    <Box
                        padding={0}
                        borderRadius={radii[1]}
                        backgroundColor='white'
                    >
                        <Box padding={3}>
                            {showFastDeliveryPanel ? (
                                <FastDeliveryDetailsPanel
                                    availabilityText={availabilityText}
                                    locationText={locationText}
                                    forText={forText}
                                    sameDayAvailabilityStatus={sameDayAvailabilityStatus}
                                    availabilityTextColor={availabilityTextColor}
                                    cutoffMessage={sameDayDeliveryPanelMessage}
                                    sddRougeTestV2FreeShippingMessage={sddRougeTestV2FreeShippingMessage}
                                    nddEnabled={nddEnabled}
                                    skuTrialEligibility={skuTrialEligibility}
                                    skuTrialPeriod={skuTrialPeriod}
                                    onOpenSDULandingPage={toggleSDULandingPage}
                                    onShowSDDRougeTestV2InfoModal={handleShowSDDRougeTestV2InfoModal}
                                    onLocationClick={handleFastDeliveryLocationClick}
                                    isUnavailable={isFastDeliveryUnavailable}
                                />
                            ) : selectedMethod === DELIVERY_OPTIONS.PICKUP ? (
                                <PickupDetailsPanel
                                    currentProduct={currentProduct}
                                    storeName={storeName}
                                    availabilityLabel={availabilityLabel}
                                    availabilityColor={availabilityColor}
                                    pickupMessage={pickupMessage}
                                    pickupMessageColor={pickupMessageColor}
                                    displayOrderCutoffCountdown={displayOrderCutoffCountdown}
                                    showInStorePickup={showInStorePickup}
                                    showCurbsideIndicator={showCurbsideIndicator}
                                    showConciergeCurbsideIndicator={showConciergeCurbsideIndicator}
                                    isPickupOutOfStock={isPickupOutOfStock}
                                    showNearbyStoreAvailability={showNearbyStoreAvailability}
                                    isInStockAtNearbyStore={isInStockAtNearbyStore}
                                    isPickupUnavailableAtStore={isPickupUnavailableAtStore}
                                    isPickupUnavailableForItem={isPickupUnavailableForItem}
                                />
                            ) : (
                                <ShippingDetailsPanel
                                    etaText={panelEtaText}
                                    zipCode={preferredZipCode}
                                    currentSku={currentSku}
                                />
                            )}
                            {showAutoReplenishment && selectedMethod === DELIVERY_OPTIONS.STANDARD && (
                                <>
                                    <Box
                                        marginTop={2}
                                        marginBottom={2}
                                        css={styles.divider}
                                    />
                                    <AutoReplenishRow
                                        currentSku={currentSku}
                                        commerce={commerce}
                                        isReplenishmentEligible={isReplenishmentEligible}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                </Flex>
            </Flex>
            {renderSDULandingPage && (
                <SDULandingPageModal
                    isOpen={renderSDULandingPage}
                    onDismiss={toggleSDULandingPage}
                    mediaId={sduMediaId}
                    skuTrialPeriod={skuTrialPeriod}
                    isSDUAddedToBasket={isSDUAddedToBasket}
                    isUserSDUTrialEligible={isUserSduTrialEligible}
                    isCanada={languageLocale.isCanada()}
                    skipConfirmationModal={false}
                    isUserSDUTrialAllowed={isUserSDUTrialAllowed}
                />
            )}
        </Box>
    );
};

FulfillmentMethods.propTypes = {
    currentProduct: PropTypes.object,
    fulfillment: PropTypes.object,
    commerce: PropTypes.object,
    displayOrderCutoffCountdown: PropTypes.bool
};

export default wrapFunctionalComponent(FulfillmentMethods, 'FulfillmentMethods');
